<!-- Market Information — market EVIDENCE for this registration, in four cards:
     (1) Observable Cohort Value (cleaned-cohort quartile band, a signal not a
     valuation), (2) Previous Sale in Context (own latest sale vs cohort median
     that year), (3) Verified Sale History (Chart B cohort scatter with this
     plate's own gold sales), (4) Segment Performance (Chart A modelled index,
     no sale markers). Quiet fallback when neither chart applies. -->
<section class="mi-section" id="sec-market">
  <div class="wrap">
    <div class="sec-head">
      <div class="sec-rule"></div>
      <span class="section-index">Market evidence</span>
      <h2>Understanding the market</h2>
      <p class="section-lede" *ngIf="hasChart || hasCohort">
        Asking prices, verified auction sales and a price index for plates like
        {{ registration }}, shown as evidence rather than a valuation.
      </p>
    </div>

    <!-- ============ Section 1: Comparable Market (market position) ============ -->
    <!-- Chart 1 = "Where does this registration sit within today's comparable
         market?". The cloud is ALWAYS live advertised comparables at their real
         asking prices. The subject is highlighted at its own asking price when
         advertised; otherwise a SEPARATE cohort market-signal marker is shown.
         Never plots verified sales (that is Chart 2). -->
    <div class="mi-evidence-card" *ngIf="comparable as cm">
      <!-- header: title + subtitle left, headline figures right -->
      <div class="ic-head">
        <div class="ic-head-text">
          <span class="ic-title">{{ cm.heading }}</span>
          <p class="ic-sub">{{ cm.subtitle }}</p>
        </div>
        <div class="ic-figs" *ngIf="cm.hasChart && cm.liveMedian" [attr.aria-label]="cm.liveLabel">
          <div class="ic-fig" *ngIf="cm.liveLo">
            <span class="ic-fig-k">Lowest asking</span>
            <span class="ic-fig-v">{{ cm.liveLo }}</span>
          </div>
          <div class="ic-fig">
            <span class="ic-fig-k">Median asking</span>
            <span class="ic-fig-v">{{ cm.liveMedian }}</span>
          </div>
          <div class="ic-fig" *ngIf="cm.liveHi">
            <span class="ic-fig-k">Highest asking</span>
            <span class="ic-fig-v">{{ cm.liveHi }}</span>
          </div>
        </div>
      </div>

      <!-- chart (only when there are live comparables to plot) -->
      <ng-container *ngIf="cm.hasChart; else cmEmpty">

      <div class="chart-scroll" #chartScroll>
      <svg
        class="scatter-svg"
        [attr.viewBox]="cm.viewBox"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        [attr.aria-label]="cm.subtitle"
      >
        <!-- strip background (click to dismiss an open tooltip) -->
        <rect
          [attr.x]="cm.plot.x"
          [attr.y]="cm.plot.y"
          [attr.width]="cm.plot.w"
          [attr.height]="cm.plot.h"
          fill="#FCFBF8"
          (click)="hideCompTip()"
        />

        <!-- live-market interquartile band (subtle positioning context) -->
        <rect
          *ngIf="cm.iqrBand as iqr"
          [attr.x]="iqr.x"
          [attr.y]="cm.plot.y"
          [attr.width]="iqr.w"
          [attr.height]="cm.plot.h"
          fill="#0F6B49"
          fill-opacity="0.07"
        />

        <!-- live-market median reference line -->
        <line
          *ngIf="cm.medianX as mx"
          [attr.x1]="mx"
          [attr.y1]="cm.plot.y"
          [attr.x2]="mx"
          [attr.y2]="cm.plot.y + cm.plot.h"
          stroke="#0F6B49"
          stroke-width="1.2"
          stroke-dasharray="3 3"
          opacity="0.55"
        />

        <!-- wider same-format evidence: other NN-L registrations (any letter),
             quiet grey dots BEHIND the close-cohort dots for market density.
             Each carries the same enlarged hit target as the green dots so it
             gets the active tooltip (reg / asking / dealer count) + click-through. -->
        <g *ngFor="let p of cm.widePoints">
          <circle
            [attr.cx]="p.cx"
            [attr.cy]="p.cy"
            r="3.5"
            class="mi-dot-comp"
          />
          <circle
            [attr.cx]="p.cx"
            [attr.cy]="p.cy"
            r="12"
            fill="transparent"
            class="mi-dot-hit"
            (mouseenter)="showCompTip(p)"
            (mouseleave)="hideCompTip()"
            (click)="openComp(p)"
          >
            <title>{{ p.regFmt }}: asking {{ p.priceLabel }}</title>
          </circle>
        </g>

        <!-- comparable registration dots: each carries an enlarged invisible
             hit target for hover (tooltip) + click-through to its page. -->
        <g *ngFor="let p of cm.points">
          <circle
            [attr.cx]="p.cx"
            [attr.cy]="p.cy"
            r="4.5"
            class="mi-dot-primary"
          />
          <circle
            [attr.cx]="p.cx"
            [attr.cy]="p.cy"
            r="14"
            fill="transparent"
            class="mi-dot-hit"
            (mouseenter)="showCompTip(p)"
            (mouseleave)="hideCompTip()"
            (click)="openComp(p)"
          >
            <title>{{ p.regFmt }}: asking {{ p.priceLabel }}</title>
          </circle>
        </g>

        <!-- x-axis price labels -->
        <text
          *ngFor="let t of cm.xTicks"
          [attr.x]="t.x"
          [attr.y]="cm.plot.y + cm.plot.h + 22"
          text-anchor="middle"
          class="mi-axis"
        >
          {{ t.label }}
        </text>

        <!-- subject: this registration at its ACTUAL asking price (solid gold) -->
        <g *ngIf="cm.subject as su">
          <line
            [attr.x1]="su.cx"
            [attr.y1]="cm.plot.y"
            [attr.x2]="su.cx"
            [attr.y2]="su.axisY"
            stroke="#1a1500"
            stroke-width="1"
            stroke-dasharray="2 2"
            opacity="0.45"
          />
          <circle
            [attr.cx]="su.cx"
            [attr.cy]="su.cy"
            r="7.5"
            fill="#FFD100"
            stroke="#1a1500"
            stroke-width="1.6"
          >
            <title>{{ su.label }}: {{ su.sub }}</title>
          </circle>
          <text
            [attr.x]="su.cx"
            [attr.y]="su.cy - 14"
            text-anchor="middle"
            class="mi-own-label"
          >
            {{ su.label }}
          </text>
          <!-- Sub-line sits ABOVE the reg label (not below the marker) so it
               never lands on the comparable dots underneath. -->
          <text
            [attr.x]="su.cx"
            [attr.y]="su.cy - 30"
            text-anchor="middle"
            class="mi-own-when mi-halo"
          >
            {{ su.sub }}
          </text>
        </g>

        <!-- live-market signal: SEPARATE hollow/dashed gold marker, shown only
             when this registration is not advertised. Position derived from the
             current live listings only — clearly a signal, not a valuation. -->
        <g *ngIf="cm.signal as sg">
          <line
            [attr.x1]="sg.cx"
            [attr.y1]="cm.plot.y"
            [attr.x2]="sg.cx"
            [attr.y2]="sg.axisY"
            stroke="#1a1500"
            stroke-width="1"
            stroke-dasharray="2 2"
            opacity="0.4"
          />
          <circle
            [attr.cx]="sg.cx"
            [attr.cy]="sg.cy"
            r="8"
            fill="#FFFFFF"
            stroke="#D4A000"
            stroke-width="2"
            stroke-dasharray="3 2"
          >
            <title>{{ sg.label }}: {{ sg.sub }}</title>
          </circle>
          <text
            [attr.x]="sg.cx"
            [attr.y]="sg.cy - 14"
            text-anchor="middle"
            class="mi-own-label"
          >
            Market signal
          </text>
          <text
            [attr.x]="sg.cx"
            [attr.y]="sg.cy + 22"
            text-anchor="middle"
            class="mi-own-when"
          >
            {{ sg.sub }}
          </text>
        </g>

        <!-- interactive tooltip (reg / asking / dealer count) -->
        <g *ngIf="activeCompTip as tip" class="mi-tip" pointer-events="none">
          <rect
            [attr.x]="tip.x"
            [attr.y]="tip.y"
            [attr.width]="tip.w"
            [attr.height]="tip.h"
            rx="6"
            class="mi-tip-box"
          />
          <text
            *ngFor="let ln of tip.lines; let i = index"
            [attr.x]="tip.x + 11"
            [attr.y]="tip.y + 19 + i * 16"
            [attr.class]="ln.head ? 'mi-tip-head' : 'mi-tip-line'"
          >
            {{ ln.t }}
          </text>
        </g>
      </svg>
      </div>

      <div class="scatter-legend">
        <span class="sl-item"><span class="sl-dot primary"></span>{{ cm.sourceLabel }}</span>
        <span class="sl-item" *ngIf="cm.wideLabel"><span class="sl-dot comp"></span>{{ cm.wideLabel }}</span>
        <span class="sl-item"><span class="sl-band"></span>Middle half of asking prices</span>
        <span class="sl-item" *ngIf="cm.medianX"><span class="sl-line median"></span>Median asking price</span>
        <span class="sl-item" *ngIf="cm.subject">
          <span class="sl-dot self"></span>{{ registration }} asking price
        </span>
        <span class="sl-item" *ngIf="cm.signal">
          <span class="sl-dot self"></span>{{ registration }} live-market signal
        </span>
      </div>

      <div class="mi-source">
        <p class="mi-kind">{{ cm.tag }} · {{ cm.countLabel }}</p>
        <p>{{ cm.source }}</p>
        <details class="mi-method">
          <summary>How this is calculated</summary>
          <p>{{ cm.framing }}</p>
          <p>{{ cm.liveBasis }} {{ cm.disclaimer }}</p>
        </details>
      </div>
      </ng-container>

      <!-- empty / loading state (no live comparables to plot) -->
      <ng-template #cmEmpty>
        <p class="cm-framing">{{ cm.emptyNote }}</p>
        <div class="mi-source">
          <p class="mi-kind">{{ cm.tag }} · {{ cm.countLabel }}</p>
          <p>{{ cm.liveBasis }} {{ cm.disclaimer }}</p>
        </div>
      </ng-template>
    </div>

    <!-- ============ Verified Sale History (Chart B) ============
         The "Previous Sale in Context" summary now lives INSIDE this section as
         a compact sale-record panel above the scatter, so the whole block reads
         as one evidence story (record → chart → methodology). -->
    <div class="scatter-card" *ngIf="cohort as cc">
      <div class="ic-head">
        <div class="ic-head-text">
          <span class="ic-title">Verified auction sales</span>
          <p class="ic-sub">{{ cc.blurb }}</p>
        </div>
      </div>

      <!-- Compact sale-record panel: this registration's own latest verified
           sale, read like an auction-catalogue line. The sale price is the
           dominant fact; relative position is supporting context, never the
           headline. Built from existing PrevSaleVM data — no new calculation. -->
      <div class="vsh-record" *ngIf="prevSale as ps">
        <div class="vsh-fact vsh-fact--primary">
          <span class="vsh-k">Last verified sale</span>
          <span class="vsh-v">{{ ps.price }}</span>
        </div>
        <div class="vsh-fact">
          <span class="vsh-k">Sale date</span>
          <span class="vsh-v">{{ ps.date }}</span>
        </div>
        <div class="vsh-fact" *ngIf="ps.cohortMedian">
          <span class="vsh-k">Cohort median at sale</span>
          <span class="vsh-v">{{ ps.cohortMedian }}</span>
        </div>
        <div class="vsh-fact">
          <span class="vsh-k">Relative position</span>
          <span class="vsh-v vsh-delta" [attr.data-dir]="ps.deltaDir">{{ ps.deltaLabel }}</span>
        </div>
      </div>

      <div class="chart-scroll">
      <svg
        class="scatter-svg"
        [attr.viewBox]="cc.viewBox"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        [attr.aria-label]="cc.title"
      >
        <!-- plot background (tap to dismiss an open tooltip) -->
        <rect
          [attr.x]="cc.plot.x"
          [attr.y]="cc.plot.y"
          [attr.width]="cc.plot.w"
          [attr.height]="cc.plot.h"
          fill="#FCFBF8"
          (click)="hideCohortTip()"
        />

        <!-- horizontal gridlines + £ labels -->
        <g>
          <line
            *ngFor="let g of cc.gridlines"
            [attr.x1]="cc.plot.x"
            [attr.y1]="g.y"
            [attr.x2]="cc.plot.x + cc.plot.w"
            [attr.y2]="g.y"
            stroke="#EFEDE8"
            stroke-width="1"
          />
          <text
            *ngFor="let g of cc.gridlines"
            [attr.x]="cc.plot.x - 8"
            [attr.y]="g.y + 3"
            text-anchor="end"
            class="mi-axis"
          >
            {{ g.label }}
          </text>
        </g>

        <!-- x-axis year labels -->
        <text
          *ngFor="let t of cc.xTicks"
          [attr.x]="t.x"
          [attr.y]="cc.plot.y + cc.plot.h + 18"
          text-anchor="middle"
          class="mi-axis"
        >
          {{ t.label }}
        </text>

        <!-- 2021 shared reference line -->
        <g *ngIf="cc.refLine as ref">
          <line
            [attr.x1]="ref.x"
            [attr.y1]="cc.plot.y"
            [attr.x2]="ref.x"
            [attr.y2]="cc.plot.y + cc.plot.h"
            stroke="#D4A000"
            stroke-width="1.2"
            stroke-dasharray="4 3"
            opacity="0.7"
          />
          <text
            [attr.x]="ref.x"
            [attr.y]="cc.plot.y - 5"
            text-anchor="middle"
            class="mi-axis mi-axis--amber"
          >
            2021
          </text>
        </g>

        <!-- clearance dots: primary (green) + sister (grey).
             Each dot has an enlarged invisible hit circle for reliable
             tap/hover (tooltip below). Native <title> kept for a11y. -->
        <g *ngFor="let d of cc.dots">
          <circle
            [attr.cx]="d.cx"
            [attr.cy]="d.cy"
            r="4.5"
            [attr.class]="d.isSister ? 'mi-dot-sister' : 'mi-dot-primary'"
          />
          <circle
            [attr.cx]="d.cx"
            [attr.cy]="d.cy"
            r="14"
            fill="transparent"
            class="mi-dot-hit"
            (mouseenter)="showCohortTip(d)"
            (mouseleave)="hideCohortTip()"
            (click)="showCohortTip(d)"
          >
            <title>{{ d.title }}</title>
          </circle>
        </g>

        <!-- cohort annual median line + nodes -->
        <path
          [attr.d]="cc.medianPath"
          fill="none"
          stroke="#0F6B49"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          opacity="0.85"
        />
        <circle
          *ngFor="let n of cc.medianNodes"
          [attr.cx]="n.cx"
          [attr.cy]="n.cy"
          r="3"
          fill="#0F6B49"
        />
        <text
          *ngFor="let l of cc.medianLabels"
          [attr.x]="l.x"
          [attr.y]="l.y"
          text-anchor="middle"
          class="mi-median-label"
        >
          {{ l.text }}
        </text>

        <!-- dashed gold connector through this plate's own clearances
             (only when there is more than one own verified sale) -->
        <path
          *ngIf="cc.ownConnectorPath"
          [attr.d]="cc.ownConnectorPath"
          fill="none"
          stroke="#D4A000"
          stroke-width="1.4"
          stroke-dasharray="4 3"
          opacity="0.8"
        />

        <!-- this plate's own clearances (gold). Only the latest carries the
             dashed drop-tick, callout label and axis date, to avoid clutter. -->
        <g *ngFor="let own of cc.owns">
          <line
            *ngIf="own.isLatest"
            [attr.x1]="own.cx"
            [attr.y1]="own.cy"
            [attr.x2]="own.cx"
            [attr.y2]="own.tickY"
            stroke="#1a1500"
            stroke-width="1"
            stroke-dasharray="2 2"
            opacity="0.5"
          />
          <circle
            [attr.cx]="own.cx"
            [attr.cy]="own.cy"
            [attr.r]="own.isLatest ? 7 : 5.5"
            fill="#FFD100"
            stroke="#1a1500"
            stroke-width="1.6"
          >
            <title>{{ own.title }}</title>
          </circle>
          <text
            *ngIf="own.isLatest"
            [attr.x]="own.cx"
            [attr.y]="own.cy - 13"
            [attr.text-anchor]="own.anchor"
            class="mi-own-label"
          >
            {{ own.label }}
          </text>
          <text
            *ngIf="own.isLatest"
            [attr.x]="own.cx"
            [attr.y]="cc.plot.y + cc.plot.h + 32"
            [attr.text-anchor]="own.anchor"
            class="mi-own-when"
          >
            {{ own.when }}
          </text>
        </g>

        <!-- interactive tooltip (tap on mobile / hover on desktop) -->
        <g *ngIf="activeTip as tip" class="mi-tip" pointer-events="none">
          <rect
            [attr.x]="tip.x"
            [attr.y]="tip.y"
            [attr.width]="tip.w"
            [attr.height]="tip.h"
            rx="6"
            class="mi-tip-box"
          />
          <text
            *ngFor="let ln of tip.lines; let i = index"
            [attr.x]="tip.x + 11"
            [attr.y]="tip.y + 19 + i * 16"
            [attr.class]="ln.head ? 'mi-tip-head' : 'mi-tip-line'"
          >
            {{ ln.t }}
          </text>
        </g>
      </svg>
      </div>

      <div class="scatter-legend">
        <span class="sl-item">
          <span class="sl-dot primary"></span>{{ cc.primaryLabel }}
        </span>
        <span class="sl-item" *ngIf="cc.hasSister">
          <span class="sl-dot sister"></span>{{ cc.sisterLabel }}
        </span>
        <span class="sl-item">
          <span class="sl-line median"></span>Median for each year
        </span>
        <span class="sl-item" *ngIf="cc.owns.length">
          <span class="sl-dot self"></span>{{ registration }} verified sale
        </span>
      </div>

      <div class="mi-source">
        <p class="mi-kind">Verified sales · {{ cc.metaRange }}</p>
        <p>{{ cc.source }}</p>
        <details class="mi-method">
          <summary>How this is calculated</summary>
          <p>{{ cc.note }} The final year may be a partial year.</p>
        </details>
      </div>
    </div>

    <!-- ============ Section 4: Segment Performance (Chart A) ============ -->
    <div class="scatter-card" *ngIf="chart as c">
      <!-- header: title + subtitle left, the index's computed performance right -->
      <div class="ic-head">
        <div class="ic-head-text">
          <span class="ic-title">{{ c.title }}</span>
          <p class="ic-sub">{{ c.sub }}</p>
        </div>
        <div class="ic-figs" *ngIf="c.figures.length">
          <div class="ic-fig" *ngFor="let f of c.figures">
            <span class="ic-fig-k">{{ f.k }}</span>
            <span class="ic-fig-v ic-trend" [attr.data-dir]="f.dir">{{ f.v }}</span>
          </div>
        </div>
      </div>

      <div class="chart-scroll">
      <svg
        class="scatter-svg"
        [attr.viewBox]="c.viewBox"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        [attr.aria-label]="c.title"
      >
        <!-- plot background -->
        <rect
          [attr.x]="c.plot.x"
          [attr.y]="c.plot.y"
          [attr.width]="c.plot.w"
          [attr.height]="c.plot.h"
          fill="#F4F8F6"
        />

        <!-- horizontal gridlines + y labels -->
        <g>
          <line
            *ngFor="let g of c.gridlines"
            [attr.x1]="c.plot.x"
            [attr.y1]="g.y"
            [attr.x2]="c.plot.x + c.plot.w"
            [attr.y2]="g.y"
            stroke="#EFEDE8"
            stroke-width="1"
          />
          <text
            *ngFor="let g of c.gridlines"
            [attr.x]="c.plot.x - 8"
            [attr.y]="g.y + 3"
            text-anchor="end"
            class="mi-axis"
          >
            {{ g.label }}
          </text>
        </g>

        <!-- x-axis year labels -->
        <text
          *ngFor="let t of c.xTicks"
          [attr.x]="t.x"
          [attr.y]="c.plot.y + c.plot.h + 18"
          text-anchor="middle"
          class="mi-axis"
        >
          {{ t.label }}
        </text>

        <!-- base-year reference line (index = 100) -->
        <g *ngIf="c.refLine as ref">
          <line
            [attr.x1]="ref.x"
            [attr.y1]="c.plot.y"
            [attr.x2]="ref.x"
            [attr.y2]="c.plot.y + c.plot.h"
            stroke="#D4A000"
            stroke-width="1.2"
            stroke-dasharray="4 3"
            opacity="0.7"
          />
          <text
            [attr.x]="ref.x"
            [attr.y]="c.plot.y - 5"
            text-anchor="middle"
            class="mi-axis mi-axis--amber"
          >
            {{ ref.label }}
          </text>
        </g>

        <!-- confidence-interval band -->
        <path
          *ngIf="c.ciBandPath"
          [attr.d]="c.ciBandPath"
          fill="#007850"
          fill-opacity="0.10"
        />

        <!-- index line: solid (observed) + dashed (provisional tail) -->
        <path
          [attr.d]="c.solidPath"
          fill="none"
          stroke="#007850"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          *ngIf="c.dashedPath"
          [attr.d]="c.dashedPath"
          fill="none"
          stroke="#007850"
          stroke-width="2"
          stroke-linecap="round"
          stroke-dasharray="5 3"
        />

        <!-- years above the capped axis: arrow at the top edge + real value -->
        <g *ngFor="let o of c.offScale">
          <title>{{ o.title }}</title>
          <path
            [attr.d]="'M ' + (o.x - 4) + ',' + (c.plot.y + 7) + ' L ' + o.x + ',' + (c.plot.y + 1) + ' L ' + (o.x + 4) + ',' + (c.plot.y + 7) + ' Z'"
            fill="#007850"
          />
          <text
            [attr.x]="o.x + 6"
            [attr.y]="c.plot.y + 8"
            text-anchor="start"
            class="mi-axis mi-axis--offscale"
          >
            {{ o.label }}
          </text>
        </g>

        <!-- node dots -->
        <ng-container *ngFor="let n of c.nodes">
          <circle
            *ngIf="!n.provisional"
            [attr.cx]="n.cx"
            [attr.cy]="n.cy"
            r="2.5"
            fill="#007850"
          />
          <circle
            *ngIf="n.provisional"
            [attr.cx]="n.cx"
            [attr.cy]="n.cy"
            r="2.5"
            fill="#F4F8F6"
            stroke="#007850"
            stroke-width="1.5"
          />
        </ng-container>

        <!-- Own verified sales are intentionally NOT drawn on the modelled
             index line — a modelled index value is not a transaction price.
             They appear on Chart B (Verified Sale History) in actual £. -->
      </svg>
      </div>

      <div class="scatter-legend">
        <span class="sl-item"><span class="sl-line"></span>Modelled index</span>
        <span class="sl-item"><span class="sl-band"></span>95% range</span>
      </div>

      <div class="mi-source">
        <p class="mi-kind">{{ c.tag }} · {{ c.metaRange }}</p>
        <p>{{ c.source }}</p>
        <details class="mi-method">
          <summary>How this is calculated</summary>
          <p>{{ c.blurb }}</p>
          <p>{{ c.note }} The final year is year to date (dashed).</p>
        </details>
      </div>
    </div>

    <!-- fallback -->
    <div class="data-fallback" *ngIf="unavailable">
      <b>A market index isn’t available for this registration yet.</b>
      Market performance is shown once the PlateX index has sufficient coverage
      for this segment.
    </div>
  </div>
</section>
